import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Scale, ArrowLeft, Apple } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import AvocatEnOrLogo from "../components/AvocatEnOrLogo";
import LanguageSelector from "../components/LanguageSelector";
import { toast } from "sonner";
import { isNativePlatform } from "../lib/platform";
import { signInWithApple, signInWithGoogleNative } from "../lib/socialAuth";

export default function Login() {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("mode") === "register" ? "register" : "login";
  const next = searchParams.get("next") || "/sos";
  const [tab, setTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null); // "apple" | "google" | null
  const navigate = useNavigate();
  const { loginWithCredentials, registerWithCredentials, checkAuth } = useAuth();
  // On distingue 2 modes :
  //  - Web navigateur     → bouton "Continuer avec Google" via Emergent OAuth
  //  - iOS/Android natif  → boutons "Sign in with Apple" + "Google" via plugins Capacitor
  //                         (Apple exige Sign in with Apple dès qu'un autre social login est présent — Guideline 4.8)
  const nativeMode = isNativePlatform();

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regName, setRegName] = useState("");

  const handleGoogleLoginWeb = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + next;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleAppleNative = async () => {
    setSocialLoading("apple");
    try {
      await signInWithApple();
      if (checkAuth) await checkAuth();
      toast.success("Connexion réussie");
      navigate(next);
    } catch (err) {
      // L'utilisateur a annulé la popup Apple → silence
      if (err?.code === "ERR_CANCELED" || /cancel/i.test(err?.message || "")) return;
      toast.error(err?.response?.data?.detail || err?.message || "Connexion Apple impossible");
    } finally {
      setSocialLoading(null);
    }
  };

  const handleGoogleNative = async () => {
    setSocialLoading("google");
    try {
      await signInWithGoogleNative();
      if (checkAuth) await checkAuth();
      toast.success("Connexion réussie");
      navigate(next);
    } catch (err) {
      if (/cancel|12501/i.test(err?.message || "")) return;
      toast.error(err?.response?.data?.detail || err?.message || "Connexion Google impossible");
    } finally {
      setSocialLoading(null);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginWithCredentials(loginEmail, loginPassword);
      toast.success("Connexion réussie");
      navigate(next);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await registerWithCredentials(regEmail, regPassword, regName);
      toast.success("Compte créé");
      navigate(next);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="login-page">
      <div className="p-6 pt-3 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors h-10 px-3 -ml-3 rounded-sm hover:bg-slate-100" data-testid="back-to-home">
          <ArrowLeft className="h-4 w-4" /> Retour
        </Link>
        <LanguageSelector variant="light" compact />
      </div>
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <div className="inline-flex flex-col items-center gap-3 mb-6">
              <img
                src="/logo-seal-large.png"
                alt="Un Avocat en Or"
                width={200}
                height={200}
                style={{ width: "200px", height: "auto", display: "block" }}
                className="rounded-sm shadow-lg"
                data-testid="login-seal"
              />
            </div>
            <h1 className="font-serif-display text-3xl font-medium tracking-tight mb-2">Bienvenue</h1>
            <p className="text-sm text-slate-500">Accédez à votre assistant juridique multi-pays</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-8 shadow-sm">
            {nativeMode ? (
              <>
                {/* iOS/Android : Sign in with Apple (noir, obligatoire HIG) + Google natif */}
                <Button
                  onClick={handleAppleNative}
                  disabled={!!socialLoading}
                  className="w-full rounded-sm h-11 bg-black hover:bg-neutral-800 text-white mb-3"
                  data-testid="apple-login-button"
                >
                  <Apple className="h-4 w-4 mr-3" fill="currentColor" />
                  {socialLoading === "apple" ? "Connexion…" : "Continuer avec Apple"}
                </Button>
                <Button
                  onClick={handleGoogleNative}
                  disabled={!!socialLoading}
                  variant="outline"
                  className="w-full rounded-sm h-11 border-slate-300 mb-6"
                  data-testid="google-login-button-native"
                >
                  <svg className="h-4 w-4 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {socialLoading === "google" ? "Connexion…" : "Continuer avec Google"}
                </Button>

                <div className="relative mb-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-400 tracking-widest">ou</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Web : Google OAuth via Emergent (comportement historique) */}
                <Button
                  onClick={handleGoogleLoginWeb}
                  variant="outline"
                  className="w-full rounded-sm h-11 border-slate-300 mb-6"
                  data-testid="google-login-button"
                >
                  <svg className="h-4 w-4 mr-3" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Continuer avec Google
                </Button>

                <div className="relative mb-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-200"></div>
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-400 tracking-widest">ou</span>
                  </div>
                </div>
              </>
            )}

            <Tabs value={tab} onValueChange={setTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 rounded-sm">
                <TabsTrigger value="login" className="rounded-sm" data-testid="tab-login">Connexion</TabsTrigger>
                <TabsTrigger value="register" className="rounded-sm" data-testid="tab-register">Inscription</TabsTrigger>
              </TabsList>
              <TabsContent value="login" className="mt-6">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email" className="text-xs uppercase tracking-widest">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      required
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="rounded-sm mt-2"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password" className="text-xs uppercase tracking-widest">Mot de passe</Label>
                    <Input
                      id="login-password"
                      type="password"
                      required
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="rounded-sm mt-2"
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 h-11" data-testid="login-submit-button">
                    {loading ? "Connexion..." : "Se connecter"}
                  </Button>
                </form>
              </TabsContent>
              <TabsContent value="register" className="mt-6">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="reg-name" className="text-xs uppercase tracking-widest">Nom complet</Label>
                    <Input
                      id="reg-name"
                      required
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      className="rounded-sm mt-2"
                      data-testid="register-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-email" className="text-xs uppercase tracking-widest">Email</Label>
                    <Input
                      id="reg-email"
                      type="email"
                      required
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      className="rounded-sm mt-2"
                      data-testid="register-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-password" className="text-xs uppercase tracking-widest">Mot de passe</Label>
                    <Input
                      id="reg-password"
                      type="password"
                      required
                      minLength={6}
                      value={regPassword}
                      onChange={(e) => setRegPassword(e.target.value)}
                      className="rounded-sm mt-2"
                      data-testid="register-password-input"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 h-11" data-testid="register-submit-button">
                    {loading ? "Création..." : "Créer un compte"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </div>

          <p className="text-xs text-center text-slate-400 mt-6">
            En continuant, vous acceptez que cet outil soit informatif uniquement.
          </p>
        </div>
      </div>
    </div>
  );
}
